import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  KeyRound, Plus, RefreshCw, RotateCw, Ban, Power, Trash2, Copy, ShieldCheck,
  ShieldAlert, X, Plane, CheckCircle2, Loader2, Server, Radio, Network, Settings2,
} from "lucide-react";
import DroneEnrollWizard from "@/components/DroneEnrollWizard";
import OtaAgentModal from "@/components/OtaAgentModal";
import { ArrowUpCircle } from "lucide-react";

const ENROLL_BADGE = {
  created: { color: "#737373", label: "non configuré" },
  wireguard: { color: "#3B82F6", label: "WireGuard OK" },
  ssh: { color: "#3B82F6", label: "SSH OK" },
  agent: { color: "#A855F7", label: "agent actif" },
  connected: { color: "#22C55E", label: "connecté" },
  partial: { color: "#F59E0B", label: "partiel" },
  error: { color: "#EF4444", label: "erreur" },
};
const enrollBadge = (d) => {
  if (d.wireguard_peer_configured && (d.enrollment?.ssh)) return ENROLL_BADGE.ssh;
  if (d.wireguard_peer_configured) return ENROLL_BADGE.wireguard;
  return ENROLL_BADGE[d.enrollment_status] || ENROLL_BADGE.created;
};

const GATEWAY_URL = "https://api.skycore-ai.com";

function ExpectedConfig({ droneId }) {
  const cfg = `DRONE_ID=${droneId || "DRONE-V1"}
DRONE_TOKEN=<token généré ci-dessus>
SKYCORE_GATEWAY_URL=${GATEWAY_URL}
HEARTBEAT_PATH=/drone/heartbeat
TELEMETRY_PATH=/drone/telemetry`;
  return (
    <div className="flex items-center gap-2">
      <pre data-testid="dr-expected-config" className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-sm px-2.5 py-2 font-mono text-[10px] text-[#A3A3A3] whitespace-pre-wrap break-all">{cfg}</pre>
      <button onClick={() => { navigator.clipboard?.writeText(cfg); toast.success("Config copiée"); }}
        className="self-start flex items-center gap-1 font-mono text-[10px] uppercase border border-white/15 rounded-sm px-2.5 py-2 hover:bg-white/10"><Copy size={12} /> Copier</button>
    </div>
  );
}

export default function DroneRegistry() {
  const [drones, setDrones] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [wizard, setWizard] = useState(null); // {drone} or {} for new
  const [form, setForm] = useState({ drone_id: "", model: "", series: "", serial: "", wireguard_ip: "", lan_ip: "", hostname: "", ssh_user: "droneadmin" });
  const [revealed, setRevealed] = useState(null); // {drone_id, token}
  const [otaDrone, setOtaDrone] = useState(null); // drone for OTA modal
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null); // {title, message, critical, onYes}

  const load = () => api.get("/drone-registry").then((r) => setDrones(r.data.drones)).catch((e) => { toast.error(apiErr(e)); setDrones([]); });
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const askConfirm = (cfg) => setConfirm(cfg);

  const create = async () => {
    if (!form.drone_id.trim()) return toast.error("drone_id requis");
    setBusy("create");
    try {
      await api.post("/drone-registry", form);
      toast.success("Drone créé");
      setShowCreate(false); setForm({ drone_id: "", model: "", series: "", serial: "", wireguard_ip: "", lan_ip: "", hostname: "", ssh_user: "droneadmin" });
      load();
    } catch (e) { toast.error(apiErr(e, "Création impossible")); } finally { setBusy(""); }
  };

  const tokenAction = async (d, action, label) => {
    setBusy(d.id);
    try {
      const { data } = await api.post(`/drone-registry/${d.id}/${action}`);
      if (data.token) setRevealed({ drone_id: d.drone_id, token: data.token });
      toast.success(`${label} — ${d.drone_id}`);
      load();
    } catch (e) { toast.error(apiErr(e, "Action impossible")); } finally { setBusy(""); }
  };

  const del = async (d) => {
    setBusy(d.id);
    try { await api.delete(`/drone-registry/${d.id}`); toast.success("Drone supprimé"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Token copié"); };
  const connColor = (s) => ({ online: "#22C55E", degraded: "#F59E0B", link_only: "#3B82F6", disabled: "#525252", offline: "#EF4444" }[s] || "#EF4444");
  const connLabel = (s) => ({ online: "EN LIGNE", degraded: "DÉGRADÉ", link_only: "LIAISON", disabled: "DÉSACTIVÉ", offline: "HORS LIGNE" }[s] || (s || "—").toUpperCase());
  const verBadge = (s) => ({ up_to_date: { label: "À jour", color: "#22C55E" }, update_available: { label: "MAJ disponible", color: "#F59E0B" } }[s] || { label: "Version inconnue", color: "#525252" });

  return (
    <div className="p-5" data-testid="drone-registry-page">
      <div className="flex items-start gap-3 flex-wrap mb-5">
        <div className="w-9 h-9 border border-white/15 rounded-sm flex items-center justify-center"><KeyRound size={18} className="text-[#3B82F6]" /></div>
        <div>
          <h1 className="font-chivo text-2xl font-black tracking-tighter uppercase leading-none">Drones &amp; Tokens</h1>
          <p className="font-mono text-[11px] text-[#A3A3A3] mt-1">Identité et authentification par drone. Chaque drone possède son propre token révocable individuellement.</p>
        </div>
        <button data-testid="dr-enroll-open" onClick={() => setWizard({})}
          className="ml-auto flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-3 py-2 hover:bg-[#3B82F6]/85"><Network size={14} /> Connecter un drone</button>
        <button data-testid="dr-create-open" onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10"><Plus size={14} /> Ajout rapide</button>
        <button data-testid="dr-reload" onClick={load} className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10"><RefreshCw size={13} /> Rafraîchir</button>
      </div>

      {/* Architecture de connexion — passerelle Hetzner obligatoire */}
      <div data-testid="dr-architecture" className="bg-[#141414] border border-[#3B82F6]/25 rounded-sm p-4 mb-5">
        <div className="flex items-center gap-2 mb-2">
          <Server size={15} className="text-[#3B82F6]" />
          <span className="font-chivo font-black uppercase tracking-tight text-[13px]">Architecture de connexion drone</span>
        </div>
        <p className="font-mono text-[11px] text-[#E5E5E5] leading-relaxed">
          Le Raspberry se connecte <b className="text-white">via la passerelle Hetzner</b> <code className="text-[#3B82F6]">{GATEWAY_URL}</code>,
          qui relaie ensuite la télémétrie vers le backend et l'interface. Il ne doit <b>jamais</b> viser l'interface directement.
        </p>
        <div className="flex items-center gap-2 flex-wrap font-mono text-[10px] uppercase tracking-wider text-[#737373] mt-2.5">
          <span className="flex items-center gap-1 text-[#A3A3A3]"><Radio size={11} className="text-[#3B82F6]" /> Raspberry</span>
          <span className="text-[#3B82F6]">→</span>
          <span className="text-[#A3A3A3]">api.skycore-ai.com (Hetzner)</span>
          <span className="text-[#3B82F6]">→</span>
          <span className="text-[#A3A3A3]">Backend Emergent</span>
          <span className="text-[#3B82F6]">→</span>
          <span className="text-[#A3A3A3]">Interface web</span>
        </div>
        <p className="font-mono text-[9px] text-[#525252] mt-2">
          Endpoints production : <code>POST /drone/heartbeat</code> · <code>POST /drone/telemetry</code> — auth <code>Bearer DRONE_TOKEN</code> (jamais x-api-key).
        </p>
      </div>

      <div className="bg-[#141414] border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="font-mono text-[9px] uppercase tracking-wider text-[#525252] border-b border-white/10">
              <th className="px-3 py-2.5">Drone</th><th className="px-3 py-2.5">Modèle / Série</th><th className="px-3 py-2.5">WireGuard</th>
              <th className="px-3 py-2.5">Token</th><th className="px-3 py-2.5">Agent</th><th className="px-3 py-2.5">Statut</th><th className="px-3 py-2.5">Dernière connexion</th><th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="dr-list">
            {drones == null && <tr><td colSpan={8} className="px-3 py-6 font-mono text-[11px] text-[#525252]">Chargement…</td></tr>}
            {drones?.length === 0 && <tr><td colSpan={8} className="px-3 py-6 font-mono text-[11px] text-[#525252]">Aucun drone enregistré. Cliquez « Nouveau drone ».</td></tr>}
            {drones?.map((d) => (
              <tr key={d.id} data-testid={`dr-row-${d.drone_id}`} className="border-b border-white/5 font-mono text-[11px]">
                <td className="px-3 py-2.5"><div className="flex items-center gap-2"><Plane size={13} className="text-[#3B82F6]" /><span className="text-white">{d.drone_id}</span></div><div className="text-[9px] text-[#525252]">{d.hostname}</div></td>
                <td className="px-3 py-2.5 text-[#A3A3A3]">{d.model || "—"} {d.series ? `· ${d.series}` : ""}</td>
                <td className="px-3 py-2.5">
                  <div className="text-[#A3A3A3]">{d.wireguard_ip || "—"}</div>
                  <span data-testid={`dr-enroll-${d.drone_id}`} className="inline-flex items-center gap-1 text-[8px] uppercase font-bold px-1 rounded-sm mt-1" style={{ color: enrollBadge(d).color, border: `1px solid ${enrollBadge(d).color}44` }}>{enrollBadge(d).label}</span>
                </td>
                <td className="px-3 py-2.5">{d.has_token ? <span className="text-[#A3A3A3]">{d.token_preview}</span> : <span className="text-[#525252]">aucun</span>}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-col gap-1">
                    <span className="text-[#A3A3A3]">{d.software_version ? `v${d.software_version}` : "—"}</span>
                    <span data-testid={`dr-agent-version-${d.drone_id}`} className="inline-flex items-center gap-1 text-[8px] uppercase font-bold px-1 rounded-sm w-fit" style={{ color: verBadge(d.agent_update_status).color, border: `1px solid ${verBadge(d.agent_update_status).color}33` }} title={d.latest_available_version ? `Référence v${d.latest_available_version}` : ""}>
                      {verBadge(d.agent_update_status).label}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-col gap-1">
                    <span data-testid={`dr-conn-${d.drone_id}`} className="inline-flex items-center gap-1.5 uppercase text-[9px] font-bold" style={{ color: connColor(d.status) }}>
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: connColor(d.status) }} /> {connLabel(d.status)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[8px] uppercase" style={{ color: d.active ? "#22C55E" : "#EF4444" }}>
                      {d.active ? <ShieldCheck size={10} /> : <ShieldAlert size={10} />} {d.active ? "token actif" : "inactif"}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-[#737373] text-[10px]">
                  {d.last_seen ? (
                    new Date(d.last_seen).toLocaleString("fr-FR")
                  ) : d.has_token && d.active ? (
                    <span data-testid={`dr-waiting-${d.drone_id}`} className="text-[#F59E0B] inline-flex items-center gap-1">
                      <Loader2 size={10} className="animate-spin" /> En attente via api.skycore-ai.com
                    </span>
                  ) : (
                    "jamais"
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    <Btn testid={`dr-configure-${d.drone_id}`} onClick={() => setWizard({ drone: d })} color="#3B82F6" icon={Settings2}>Configurer</Btn>
                    <Btn testid={`dr-ota-${d.drone_id}`} onClick={() => setOtaDrone(d)} color="#A855F7" icon={ArrowUpCircle}>Agent OTA</Btn>
                    {!d.has_token ? (
                      <Btn testid={`dr-generate-${d.drone_id}`} onClick={() => tokenAction(d, "generate-token", "Token généré")} busy={busy === d.id} color="#3B82F6" icon={KeyRound}>Générer token</Btn>
                    ) : (
                      <Btn testid={`dr-rotate-${d.drone_id}`} onClick={() => askConfirm({ title: `Rotation du token — ${d.drone_id}`, critical: true, message: "L'ancien token sera immédiatement invalidé. Le drone devra recevoir le nouveau token.", onYes: () => tokenAction(d, "rotate-token", "Token régénéré") })} busy={busy === d.id} color="#F59E0B" icon={RotateCw}>Régénérer</Btn>
                    )}
                    {d.has_token && (
                      <Btn testid={`dr-revoke-${d.drone_id}`} onClick={() => askConfirm({ title: `Révoquer le token — ${d.drone_id}`, critical: true, message: "Le token sera invalidé et le drone désactivé. Il ne pourra plus s'authentifier.", onYes: () => tokenAction(d, "revoke-token", "Token révoqué") })} busy={busy === d.id} color="#EF4444" icon={Ban}>Révoquer</Btn>
                    )}
                    {d.active ? (
                      <Btn testid={`dr-deactivate-${d.drone_id}`} onClick={() => tokenAction(d, "deactivate", "Drone désactivé")} busy={busy === d.id} icon={Power}>Désactiver</Btn>
                    ) : d.has_token ? (
                      <Btn testid={`dr-activate-${d.drone_id}`} onClick={() => tokenAction(d, "activate", "Drone activé")} busy={busy === d.id} color="#22C55E" icon={Power}>Activer</Btn>
                    ) : null}
                    <Btn testid={`dr-delete-${d.drone_id}`} onClick={() => askConfirm({ title: `Supprimer ${d.drone_id}`, critical: true, message: "Suppression définitive du drone et de son token.", onYes: () => del(d) })} busy={busy === d.id} icon={Trash2}>Suppr.</Btn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {wizard && (
        <DroneEnrollWizard
          existingDrone={wizard.drone || null}
          onCreated={load}
          onClose={() => { setWizard(null); load(); }}
        />
      )}

      {otaDrone && <OtaAgentModal drone={otaDrone} onClose={() => setOtaDrone(null)} />}

      {/* Create modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Nouveau drone" testid="dr-create-modal">
          <div className="space-y-2.5">
            <Field label="drone_id *" v={form.drone_id} on={(v) => setForm({ ...form, drone_id: v })} testid="dr-field-drone_id" placeholder="DRONE-V1" />
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Modèle" v={form.model} on={(v) => setForm({ ...form, model: v })} testid="dr-field-model" placeholder="Raspberry Pi 5" />
              <Field label="Série" v={form.series} on={(v) => setForm({ ...form, series: v })} testid="dr-field-series" />
            </div>
            <Field label="N° de série (serial)" v={form.serial} on={(v) => setForm({ ...form, serial: v })} testid="dr-field-serial" />
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="IP WireGuard" v={form.wireguard_ip} on={(v) => setForm({ ...form, wireguard_ip: v })} testid="dr-field-wg" placeholder="10.50.0.1" />
              <Field label="IP LAN" v={form.lan_ip} on={(v) => setForm({ ...form, lan_ip: v })} testid="dr-field-lan" placeholder="192.168.1.50" />
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Hostname" v={form.hostname} on={(v) => setForm({ ...form, hostname: v })} testid="dr-field-hostname" />
              <Field label="SSH user" v={form.ssh_user} on={(v) => setForm({ ...form, ssh_user: v })} testid="dr-field-ssh" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowCreate(false)} className="font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10">Annuler</button>
              <button data-testid="dr-create-submit" onClick={create} disabled={busy === "create"} className="font-mono text-[10px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-4 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-50">{busy === "create" ? "…" : "Créer"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Token reveal modal (once) */}
      {revealed && (
        <Modal onClose={() => setRevealed(null)} title={`Token — ${revealed.drone_id}`} testid="dr-token-modal">
          <div className="space-y-3">
            <div className="flex items-start gap-2 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm p-2.5">
              <ShieldAlert size={16} className="text-[#F59E0B] mt-0.5 shrink-0" />
              <p className="font-mono text-[11px] text-[#F59E0B]">Copiez ce token maintenant : il ne sera <b>plus jamais affiché</b>. Stockez-le dans le <code>.env</code> du drone (DRONE_TOKEN).</p>
            </div>
            <div className="flex items-center gap-2">
              <code data-testid="dr-token-value" className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-2 font-mono text-[11px] text-[#22C55E] break-all">{revealed.token}</code>
              <button data-testid="dr-token-copy" onClick={() => copy(revealed.token)} className="flex items-center gap-1 font-mono text-[10px] uppercase border border-white/15 rounded-sm px-2.5 py-2 hover:bg-white/10"><Copy size={12} /> Copier</button>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-wider text-[#525252] mb-1.5">Configuration attendue côté Raspberry (.env)</p>
              <ExpectedConfig droneId={revealed.drone_id} />
            </div>
            <div className="flex justify-end">
              <button data-testid="dr-token-done" onClick={() => setRevealed(null)} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider bg-[#22C55E] rounded-sm px-4 py-2 hover:bg-[#22C55E]/85"><CheckCircle2 size={13} /> J'ai copié le token</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirm */}
      {confirm && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-4" data-testid="dr-confirm">
          <div className="w-full max-w-md bg-[#141414] border border-white/15 rounded-sm p-5 space-y-4">
            <div className={`flex items-center gap-2 ${confirm.critical ? "text-[#EF4444]" : "text-[#F59E0B]"}`}><ShieldAlert size={18} /><span className="font-chivo font-black uppercase tracking-tight">{confirm.title}</span></div>
            <p className="font-mono text-[12px] text-[#E5E5E5]">{confirm.message}</p>
            <div className="flex justify-end gap-2">
              <button data-testid="dr-confirm-cancel" onClick={() => setConfirm(null)} className="font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10">Annuler</button>
              <button data-testid="dr-confirm-yes" onClick={() => { const fn = confirm.onYes; setConfirm(null); fn?.(); }} className={`font-mono text-[10px] uppercase tracking-wider rounded-sm px-4 py-2 ${confirm.critical ? "bg-[#EF4444] hover:bg-[#EF4444]/85" : "bg-[#3B82F6] hover:bg-[#3B82F6]/85"}`}>Confirmer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Btn({ children, onClick, busy, color, icon: Icon, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={busy}
      className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider border rounded-sm px-2 py-1 hover:bg-white/10 disabled:opacity-40"
      style={{ borderColor: color ? `${color}55` : "rgba(255,255,255,0.15)", color: color || "#A3A3A3" }}>
      {busy ? <Loader2 size={10} className="animate-spin" /> : Icon && <Icon size={10} />} {children}
    </button>
  );
}

function Modal({ children, onClose, title, testid }) {
  return (
    <div className="fixed inset-0 z-[1900] flex items-center justify-center bg-black/70 p-4" data-testid={testid}>
      <div className="w-full max-w-md bg-[#141414] border border-white/15 rounded-sm p-5">
        <div className="flex items-center mb-4"><span className="font-chivo font-black uppercase tracking-tight">{title}</span>
          <button onClick={onClose} className="ml-auto text-[#A3A3A3] hover:text-white"><X size={16} /></button></div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, v, on, testid, placeholder }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="font-mono text-[9px] uppercase tracking-wider text-[#525252]">{label}</label>
      <input data-testid={testid} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        className="bg-[#0A0A0A] border border-white/10 rounded-sm font-mono text-[11px] px-2.5 py-2 text-white outline-none" />
    </div>
  );
}
